import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../pages-styles/project-consultancy.css';
import { usePageMeta } from '../../hooks/usePageMeta';

export default function NoGuarantees() {
  usePageMeta(
    'No Guarantees Clause - Magbity Agency Ltd',
    'Understanding consultancy limitations and responsibilities in project advisory and development services.'
  );

  useEffect(() => {
    const el = document.querySelector('.guar-3d-card');
    if (el) el.classList.add('enter');
  }, []);

  return (
    <div className="project-consultancy">
      <section className="consultancy-hero-banner guar-hero">
        <div className="consultancy-hero-overlay"></div>
        <div className="consultancy-hero-content">
          <div className="consultancy-hero-text">
            <h1>No Guarantees Clause</h1>
            <p>
              Consultancy services provide expert guidance and recommendations, but outcomes depend on multiple factors 
              beyond our control, including client implementation, market conditions, and organizational execution capabilities.
            </p>
          </div>

          <div className="guar-illustration">
            <div className="guar-3d-scene">
              <div className="guar-3d-card">
                <div className="face front">
                  <svg width="220" height="220" viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="guarGrad" x1="0" x2="1">
                        <stop offset="0%" stopColor="#16c784" />
                        <stop offset="100%" stopColor="#00d4ff" />
                      </linearGradient>
                    </defs>
                    <rect x="10" y="10" width="200" height="200" rx="16" fill="url(#guarGrad)" opacity="0.12" />
                    <g transform="translate(45,40) scale(0.9)">
                      <path d="M35 5 L50 25 L65 5 L75 20 L60 35 L75 50 L50 40 L35 60 L30 40 L10 45 L25 30 L5 15 Z" fill="none" stroke="#fff" strokeWidth="2" opacity="0.14" />
                      <circle cx="40" cy="35" r="15" fill="none" stroke="#fff" strokeWidth="2" opacity="0.1" />
                    </g>
                  </svg>
                  <div className="card-label">Understanding Limits</div>
                </div>
                <div className="face back">
                  <h4>Realistic Expectations</h4>
                  <p>Guidance and expertise, not guaranteed financial outcomes or project success.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="consultancy-services guar-details">
        <h2>What Consultancy Does NOT Guarantee</h2>
        <div className="services-list">
          <div className="service-item left">
            <div className="service-content">
              <h3>Financial Returns or Profitability</h3>
              <p>
                Consultancy recommendations are based on market analysis and best practices, but do not guarantee 
                financial success. Business outcomes depend on market conditions, execution quality, and factors beyond consultant control.
              </p>
              <ul className="service-details-list">
                <li>Market conditions may change unexpectedly</li>
                <li>Implementation quality affects outcomes</li>
                <li>Competitive dynamics are unpredictable</li>
                <li>External economic factors impact results</li>
                <li>Client execution capability is critical</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-13c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0 8c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z" opacity=".2"/>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-13c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0 8c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" opacity=".2"/>
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Project Funding or Investor Acquisition</h3>
              <p>
                While our consultancy may help develop fundable project proposals, we cannot guarantee funding 
                acquisition. Investors make independent decisions based on their own criteria and risk assessment.
              </p>
              <ul className="service-details-list">
                <li>Funding decisions rest with investors/lenders</li>
                <li>Investment criteria vary by funder</li>
                <li>Market appetite for projects fluctuates</li>
                <li>Timing and economic cycles matter</li>
                <li>Project risk assessment is subjective</li>
              </ul>
            </div>
          </div>

          <div className="service-item left">
            <div className="service-content">
              <h3>Project Success or Completion</h3>
              <p>
                Project success depends on many factors: management quality, team execution, stakeholder commitment, 
                external conditions, and unforeseen challenges. Our role is advisory, not guaranteeing delivery.
              </p>
              <ul className="service-details-list">
                <li>Project management execution is critical</li>
                <li>Internal organizational factors matter</li>
                <li>Team capability affects outcomes</li>
                <li>External dependencies may create delays</li>
                <li>Risk events may require pivots</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5.04-6.71l-2.75 3.54h3.79l4.29-5.5h-3.65l2.75-3.54H7.11l4.29 5.5h3.65l-2.75 3.54h3.79z" opacity=".2"/>
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5.04-6.71l-2.75 3.54h3.79l4.29-5.5h-3.65l2.75-3.54H7.11l4.29 5.5h3.65l-2.75 3.54h3.79z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-3.31 0-6 2.69-6 6h2c0-2.21 1.79-4 4-4s4 1.79 4 4c0 2-1 3.68-2.5 4.5V15h2c2 0 3.5-1 3.5-3 0-2.21 1.79-4 4-4s4 1.79 4 4c0 2.21-1.79 4-4 4v2c3.31 0 6-2.69 6-6 0-3.31-2.69-6-6-6z" opacity=".2"/>
                <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-3.31 0-6 2.69-6 6h2c0-2.21 1.79-4 4-4s4 1.79 4 4c0 2-1 3.68-2.5 4.5V15h2c2 0 3.5-1 3.5-3 0-2.21 1.79-4 4-4s4 1.79 4 4c0 2.21-1.79 4-4 4v2c3.31 0 6-2.69 6-6 0-3.31-2.69-6-6-6z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Specific Business Outcomes or Metrics</h3>
              <p>
                While recommendations are data-driven, we cannot guarantee specific KPI achievement or measurable 
                business outcomes. Results depend on implementation quality and sustained organizational commitment.
              </p>
              <ul className="service-details-list">
                <li>KPI targets depend on implementation</li>
                <li>Metrics are influenced by internal factors</li>
                <li>Market conditions affect performance</li>
                <li>Sustained effort is required for results</li>
                <li>Monitoring and adjustment may be needed</li>
              </ul>
            </div>
          </div>

          <div className="service-item left">
            <div className="service-content">
              <h3>Risk-Free or Liability Protection</h3>
              <p>
                All projects and strategies carry inherent risks. We provide recommendations based on analysis, 
                but cannot eliminate all risk. Clients are responsible for risk assessment and mitigation.
              </p>
              <ul className="service-details-list">
                <li>Market risks cannot be eliminated</li>
                <li>Implementation risks must be managed</li>
                <li>Regulatory environment may change</li>
                <li>Technology risks must be assessed</li>
                <li>Contingency planning is essential</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.72-7 8.77V12H5V6.3l7-3.11v8.8z" opacity=".2"/>
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.72-7 8.77V12H5V6.3l7-3.11v8.8z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" opacity=".2"/>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Professional or Legal Advice</h3>
              <p>
                Consultancy recommendations do not replace professional legal, financial, or tax advice. Clients 
                should engage appropriate professionals for specific compliance and regulatory requirements.
              </p>
              <ul className="service-details-list">
                <li>Consult legal counsel for contracts</li>
                <li>Work with financial advisors on investments</li>
                <li>Engage tax professionals for compliance</li>
                <li>Obtain insurance and liability coverage</li>
                <li>Document all decisions and rationales</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="responsibility-checklist">
        <h2>Client Responsibility for Risk Management</h2>
        <div className="checklist-grid">
          <div className="checklist-item">
            <div className="checklist-icon">!</div>
            <h4>Conduct Due Diligence</h4>
            <p>Verify our recommendations independently</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">!</div>
            <h4>Risk Assessment</h4>
            <p>Evaluate risks and develop contingencies</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">!</div>
            <h4>Professional Advice</h4>
            <p>Engage legal, financial, and tax advisors</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">!</div>
            <h4>Implementation Planning</h4>
            <p>Plan execution and resource allocation</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">!</div>
            <h4>Monitoring & Adjustment</h4>
            <p>Track progress and adapt as needed</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">!</div>
            <h4>Documentation</h4>
            <p>Maintain records of decisions and rationales</p>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <h2>Understand Our Approach & Limitations</h2>
        <p>Let's discuss realistic expectations and develop a risk management strategy for your project.</p>
        <Link to="/contact" className="cta-button">Schedule Consultation</Link>
      </section>
    </div>
  );
}
